import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useSounds from '../hooks/useSounds';
import Logo from '../components/Logo';
import {
  IELTSExamDuration,
  IELTSReadingPassage,
  IELTSReadingQuestion,
  IELTSReadingEvaluation,
  READING_PASSAGES,
} from '../types/ielts';

interface LocationState {
  duration: IELTSExamDuration;
}

const IELTSReadingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { playSound } = useSounds();

  const state = location.state as LocationState;
  const duration = state?.duration || 'short';

  // Determine which passages to include based on duration
  const getPassages = (): IELTSReadingPassage[] => {
    switch (duration) {
      case 'short':
        return [READING_PASSAGES[0]];
      case 'medium':
        return [READING_PASSAGES[0], READING_PASSAGES[1]];
      case 'full':
        return READING_PASSAGES;
      default:
        return [READING_PASSAGES[0]];
    }
  };

  const [passages] = useState<IELTSReadingPassage[]>(getPassages);
  const [currentPassageIndex, setCurrentPassageIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [timeRemaining, setTimeRemaining] = useState<number>(
    passages.reduce((acc, p) => acc + p.timeMinutes, 0) * 60
  );
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [evaluation, setEvaluation] = useState<IELTSReadingEvaluation | null>(null);
  const [status, setStatus] = useState<'intro' | 'in-progress' | 'completed'>('intro');
  const [showPassage, setShowPassage] = useState(true);
  const headerRef = useRef<HTMLDivElement>(null);
  const toggleSentinelRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isToggleStuck, setIsToggleStuck] = useState(false);

  const currentPassage = passages[currentPassageIndex];
  const totalQuestions = passages.reduce((acc, p) => acc + p.questions.length, 0);

  // Block copy/paste/cut during the test
  useEffect(() => {
    const preventCopyPaste = (e: ClipboardEvent) => {
      if (status === 'in-progress') {
        e.preventDefault();
        return false;
      }
    };

    const preventContextMenu = (e: MouseEvent) => {
      if (status === 'in-progress') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('copy', preventCopyPaste);
    document.addEventListener('paste', preventCopyPaste);
    document.addEventListener('cut', preventCopyPaste);
    document.addEventListener('contextmenu', preventContextMenu);

    return () => {
      document.removeEventListener('copy', preventCopyPaste);
      document.removeEventListener('paste', preventCopyPaste);
      document.removeEventListener('cut', preventCopyPaste);
      document.removeEventListener('contextmenu', preventContextMenu);
    };
  }, [status]);

  // Timer effect
  useEffect(() => {
    if (isTimerRunning && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (timeRemaining === 0 && isTimerRunning) {
      playSound('complete');
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimerRunning, timeRemaining]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartExam = () => {
    setStatus('in-progress');
    setIsTimerRunning(true);
    playSound('startRecording');
  };

  // The passage/questions toggle pins directly beneath the header. The header's
  // height depends on how its text wraps at a given width, so measure it rather
  // than hardcoding the offset.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const measure = () => setHeaderHeight(header.offsetHeight);
    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(header);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // A zero-height sentinel marks where the toggle rests in the normal flow. Once
  // the sentinel scrolls up behind the header, the toggle has stuck and switches
  // to its compact form so it covers as little of the text as possible.
  useEffect(() => {
    const sentinel = toggleSentinelRef.current;
    if (status !== 'in-progress' || !sentinel || typeof IntersectionObserver === 'undefined') {
      setIsToggleStuck(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsToggleStuck(!entry.isIntersecting && entry.boundingClientRect.top < headerHeight);
      },
      { rootMargin: `-${headerHeight}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [status, headerHeight]);

  const switchView = (toPassage: boolean) => {
    setShowPassage(toPassage);

    // Switching from the pinned toggle means the reader is deep in one view.
    // Show the other view from its top instead of an arbitrary offset into it.
    const sentinel = toggleSentinelRef.current;
    if (isToggleStuck && sentinel) {
      window.scrollTo({
        top: sentinel.getBoundingClientRect().top + window.scrollY - headerHeight,
      });
    }
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleNextPassage = () => {
    if (currentPassageIndex < passages.length - 1) {
      setCurrentPassageIndex(prev => prev + 1);
      setShowPassage(true);
      playSound('stopRecording');
    }
  };

  const handlePreviousPassage = () => {
    if (currentPassageIndex > 0) {
      setCurrentPassageIndex(prev => prev - 1);
      setShowPassage(true);
    }
  };

  const checkAnswer = (question: IELTSReadingQuestion, userAnswer: string): boolean => {
    if (!userAnswer) return false;

    const normalizedAnswer = userAnswer.trim().toLowerCase();

    if (Array.isArray(question.correctAnswer)) {
      // For questions with multiple acceptable answers
      return question.correctAnswer.some(
        correct => normalizedAnswer.includes(correct.toLowerCase())
      );
    }

    const correctAnswer = question.correctAnswer.toLowerCase();

    // For fill in blanks and short answer, be more lenient
    if (question.type === 'fill_in_blanks' || question.type === 'short_answer') {
      return normalizedAnswer.includes(correctAnswer) || correctAnswer.includes(normalizedAnswer);
    }

    return normalizedAnswer === correctAnswer;
  };

  const handleSubmit = () => {
    setIsTimerRunning(false);

    // Calculate score
    let correctCount = 0;
    passages.forEach(passage => {
      passage.questions.forEach(question => {
        const userAnswer = answers[question.id] || '';
        if (checkAnswer(question, userAnswer)) {
          correctCount++;
        }
      });
    });

    const percentage = (correctCount / totalQuestions) * 100;

    // Convert percentage to IELTS band score (approximate)
    let bandScore: number;
    if (percentage >= 90) bandScore = 9;
    else if (percentage >= 80) bandScore = 8;
    else if (percentage >= 70) bandScore = 7;
    else if (percentage >= 60) bandScore = 6.5;
    else if (percentage >= 50) bandScore = 6;
    else if (percentage >= 40) bandScore = 5.5;
    else if (percentage >= 30) bandScore = 5;
    else if (percentage >= 20) bandScore = 4;
    else bandScore = 3;

    const mockEvaluation: IELTSReadingEvaluation = {
      overallBand: bandScore,
      correctAnswers: correctCount,
      totalQuestions: totalQuestions,
      percentageCorrect: Math.round(percentage),
      feedback: percentage >= 70
        ? 'Excellent reading comprehension! You demonstrated strong ability to understand main ideas and locate specific information.'
        : percentage >= 50
        ? 'Good effort! You show understanding of main ideas but may need practice with detailed questions and inference.'
        : 'Keep practicing! Focus on skimming for main ideas and scanning for specific details.',
      strengths: percentage >= 50
        ? ['Understanding main ideas', 'Locating specific information', 'Time management']
        : ['Attempting all questions', 'Basic comprehension'],
      improvements: percentage >= 70
        ? ['Practice with more complex academic texts', 'Work on inference questions']
        : percentage >= 50
        ? ['Improve scanning techniques', 'Practice True/False/Not Given questions', 'Build academic vocabulary']
        : ['Practice skimming for main ideas', 'Learn to manage time better', 'Build vocabulary', 'Practice all question types'],
    };

    setEvaluation(mockEvaluation);
    setStatus('completed');
    playSound('success');
  };

  const getAnsweredCount = (): number => {
    return Object.keys(answers).filter(key => answers[key] && answers[key].trim()).length;
  };

  const renderQuestion = (question: IELTSReadingQuestion, index: number) => {
    const questionNumber = passages
      .slice(0, currentPassageIndex)
      .reduce((acc, p) => acc + p.questions.length, 0) + index + 1;

    switch (question.type) {
      case 'multiple_choice':
        return (
          <div key={question.id} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="font-medium text-gray-800 mb-3">
              {questionNumber}. {question.question}
            </p>
            <div className="space-y-2">
              {question.options?.map((option, idx) => (
                <label
                  key={idx}
                  className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                    answers[question.id] === option
                      ? 'bg-emerald-100 border-2 border-emerald-500'
                      : 'bg-white border-2 border-gray-200 hover:border-emerald-300'
                  }`}
                >
                  <input
                    type="radio"
                    name={question.id}
                    value={option}
                    checked={answers[question.id] === option}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    className="mr-3"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'true_false_not_given':
        return (
          <div key={question.id} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="font-medium text-gray-800 mb-3">
              {questionNumber}. {question.question}
            </p>
            <div className="flex gap-3">
              {['True', 'False', 'Not Given'].map((option) => (
                <button
                  key={option}
                  onClick={() => handleAnswerChange(question.id, option)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    answers[question.id] === option
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white border-2 border-gray-200 hover:border-emerald-300 text-gray-700'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        );

      case 'fill_in_blanks':
      case 'short_answer':
        return (
          <div key={question.id} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="font-medium text-gray-800 mb-3">
              {questionNumber}. {question.question}
            </p>
            <input
              type="text"
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder="Type your answer..."
              className="w-full p-3 border-2 border-gray-200 bg-white text-gray-800 rounded-lg focus:border-emerald-500 focus:outline-none"
            />
          </div>
        );

      default:
        return null;
    }
  };

  const renderResults = () => {
    if (!evaluation) return null;

    return (
      <div className="space-y-6">
        {/* Score Summary */}
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Reading Test Results</h2>
          <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-emerald-100 mb-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-emerald-600">{evaluation.overallBand}</div>
              <div className="text-sm text-emerald-700">Band Score</div>
            </div>
          </div>
          <p className="text-gray-600 mb-2">
            You answered {evaluation.correctAnswers} out of {evaluation.totalQuestions} questions correctly
          </p>
          <p className="text-gray-500">({evaluation.percentageCorrect}% accuracy)</p>
        </div>

        {/* Feedback */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-800 mb-3">Feedback</h3>
          <p className="text-gray-600">{evaluation.feedback}</p>
        </div>

        {/* Strengths & Improvements */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-emerald-50 rounded-xl p-6">
            <h3 className="font-semibold text-emerald-800 mb-3">Strengths</h3>
            <ul className="space-y-2">
              {evaluation.strengths.map((strength, i) => (
                <li key={i} className="flex items-start gap-2 text-emerald-700">
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-amber-50 rounded-xl p-6">
            <h3 className="font-semibold text-amber-800 mb-3">Areas for Improvement</h3>
            <ul className="space-y-2">
              {evaluation.improvements.map((improvement, i) => (
                <li key={i} className="flex items-start gap-2 text-amber-700">
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                  </svg>
                  {improvement}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Answer Review */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Answer Review</h3>
          {passages.map((passage, pIdx) => (
            <div key={passage.id} className="mb-6">
              <h4 className="font-medium text-gray-700 mb-3">Passage {pIdx + 1}: {passage.title}</h4>
              <div className="space-y-3">
                {passage.questions.map((question, qIdx) => {
                  const questionNumber = passages
                    .slice(0, pIdx)
                    .reduce((acc, p) => acc + p.questions.length, 0) + qIdx + 1;
                  const userAnswer = answers[question.id] || 'Not answered';
                  const isCorrect = checkAnswer(question, answers[question.id] || '');
                  const correctAnswer = Array.isArray(question.correctAnswer)
                    ? question.correctAnswer.join(' or ')
                    : question.correctAnswer;

                  return (
                    <div
                      key={question.id}
                      className={`p-3 rounded-lg ${isCorrect ? 'bg-emerald-50' : 'bg-red-50'}`}
                    >
                      <div className="flex items-start justify-between">
                        <span className="font-medium text-gray-700">Q{questionNumber}</span>
                        {isCorrect ? (
                          <span className="text-emerald-600 text-sm font-medium">Correct</span>
                        ) : (
                          <span className="text-red-600 text-sm font-medium">Incorrect</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Your answer: {userAnswer}</p>
                      {!isCorrect && (
                        <p className="text-sm text-emerald-600 mt-1">Correct answer: {correctAnswer}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-center gap-4">
          <button
            onClick={() => navigate('/app')}
            className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors flex items-center gap-2"
            title="Go to Home"
          >
            <Logo size="sm" />
          </button>
          <button
            onClick={() => navigate('/app/ielts')}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
          >
            Back to IELTS
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <div ref={headerRef} className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/app/ielts')}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Exit
          </button>
          <div className="text-center">
            <h1 className="font-semibold text-gray-800">IELTS Reading Test</h1>
            <p className="text-xs text-gray-500">
              Passage {currentPassageIndex + 1} of {passages.length}
            </p>
          </div>
          <div className="text-right">
            <div className={`text-lg font-mono font-bold ${timeRemaining < 300 ? 'text-red-600' : 'text-emerald-600'}`}>
              {formatTime(timeRemaining)}
            </div>
            <p className="text-xs text-gray-500">
              {getAnsweredCount()}/{totalQuestions} answered
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Intro Screen */}
        {status === 'intro' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">IELTS Reading Test</h2>
            <p className="text-gray-600 mb-6">
              {duration === 'short' && 'Quick Practice: 1 passage with 13 questions (20 minutes)'}
              {duration === 'medium' && 'Standard Practice: 2 passages with 26 questions (40 minutes)'}
              {duration === 'full' && 'Full Exam: 3 passages with 40 questions (60 minutes)'}
            </p>
            <div className="bg-emerald-50 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
              <h3 className="font-semibold text-emerald-800 mb-2">Instructions:</h3>
              <ul className="text-sm text-emerald-700 space-y-1">
                <li>- Read each passage carefully</li>
                <li>- Answer all questions for each passage</li>
                <li>- You can toggle between passage and questions</li>
                <li>- Manage your time wisely</li>
                <li>- You can navigate between passages</li>
              </ul>
            </div>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => navigate('/app')}
                className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors flex items-center gap-2"
                title="Go to Home"
              >
                <Logo size="sm" />
              </button>
              <button
                onClick={() => navigate('/app/ielts')}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
              >
                Back to IELTS
              </button>
              <button
                onClick={handleStartExam}
                className="px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors"
              >
                Start Reading Test
              </button>
            </div>
          </div>
        )}

        {/* Exam in Progress */}
        {status === 'in-progress' && (
          <div className="space-y-6">
            <div ref={toggleSentinelRef} aria-hidden="true" className="h-0" />

            {/* Toggle Button: pinned under the header while scrolling. The wrapper
                keeps a fixed height so shrinking the pill never shifts the text
                below it, and it lets clicks through to the content around the pill. */}
            <div
              className={`sticky z-[5] !mt-0 h-12 flex justify-center pointer-events-none ${
                isToggleStuck ? 'items-start' : 'items-center'
              }`}
              style={{ top: headerHeight }}
            >
              <div
                className={`inline-flex rounded-lg pointer-events-auto transition-all duration-150 ${
                  isToggleStuck
                    ? 'mt-1 p-0.5 bg-gray-100/90 backdrop-blur shadow-md'
                    : 'p-1 bg-gray-100'
                }`}
              >
                <button
                  onClick={() => switchView(true)}
                  className={`rounded-lg font-medium transition-all duration-150 ${
                    isToggleStuck ? 'px-3 py-1 text-xs' : 'px-4 py-2'
                  } ${showPassage ? 'bg-white shadow text-emerald-600' : 'text-gray-600'}`}
                >
                  Passage
                </button>
                <button
                  onClick={() => switchView(false)}
                  className={`rounded-lg font-medium transition-all duration-150 ${
                    isToggleStuck ? 'px-3 py-1 text-xs' : 'px-4 py-2'
                  } ${!showPassage ? 'bg-white shadow text-emerald-600' : 'text-gray-600'}`}
                >
                  Questions ({currentPassage.questions.length})
                </button>
              </div>
            </div>

            {/* Passage View */}
            {showPassage && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">{currentPassage.title}</h2>
                <div className="prose prose-emerald max-w-none">
                  {currentPassage.text.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="text-gray-700 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Questions View */}
            {!showPassage && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-gray-800 mb-4">
                  Questions for: {currentPassage.title}
                </h3>
                {currentPassage.questions.map((question, index) => renderQuestion(question, index))}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <button
                onClick={handlePreviousPassage}
                disabled={currentPassageIndex === 0}
                className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                  currentPassageIndex === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Previous Passage
              </button>

              {currentPassageIndex === passages.length - 1 ? (
                <button
                  onClick={handleSubmit}
                  className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors"
                >
                  Submit Test
                </button>
              ) : (
                <button
                  onClick={handleNextPassage}
                  className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl transition-colors"
                >
                  Next Passage
                </button>
              )}
            </div>

            {/* Progress Indicator */}
            <div className="flex justify-center gap-2">
              {passages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentPassageIndex(idx);
                    setShowPassage(true);
                  }}
                  className={`w-8 h-8 rounded-full font-medium transition-colors ${
                    idx === currentPassageIndex
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {status === 'completed' && renderResults()}
      </div>
    </div>
  );
};

export default IELTSReadingPage;
